import React, { useMemo } from 'react';
import { Input, Button } from 'antd';
import { useFormik } from 'formik';
import useQueryParams from 'hooks/useQueryParams';
import AdSelect from 'components/Selects/AdSelect';
import AdsetNameSelect from 'components/Selects/AdsetNameSelect';
import CampaignSelect from 'components/Selects/CampaignSelect';
import Label from 'components/filterLabel';
import AntFilterField from 'components/form/fields/AntFilterField';
import getDefaultFilters, { fields, filterKeys } from './DefaultFilters';
import ChannelSelect from 'components/Selects/ChannelSelect';
import EventTypeSelector from 'components/Selects/EventTypeSelector';
import ProductSelector from 'components/Selects/ProductSelector';

const UserJourneyFilterForm = ({ stylesProps }) => {
  const { searchParams, removeParams, setUrlParams } = useQueryParams();

  const defaultFilters = useMemo(() => getDefaultFilters(searchParams), [searchParams]);

  const formik = useFormik({
    initialValues: defaultFilters,
    onSubmit: handleFilter,
  });

  const handleResetForm = () => {
    formik.setValues({});
    removeParams(filterKeys);
  };

  function handleFilter() {
    const filtersToSet = {
      ...formik.values,
      ...fields.reduce((acc, field) => {
        const val = formik.values[field.field]?.value;

        if (typeof val === 'string' && val.trim()) {
          acc[field.field] = val;
          acc[`${field.field}_type`] = formik.values[field.field]?.type || 'eq';
        }

        return acc;
      }, {}),
    };

    setUrlParams(filtersToSet);
  }

  return (
    <form onSubmit={formik.handleSubmit} className='w-full'>
      <div className={`${stylesProps} grid items-end gap-2`}>
        <Label value={formik.values.user_id} htmlFor='user_id' label='User ID'>
          <Input
            placeholder='Enter User ID'
            allowClear
            type='number'
            value={formik.values.user_id}
            onChange={(e) => formik.setFieldValue('user_id', e.target.value)}
          />
        </Label>
        <Label value={formik.values.session_id} htmlFor='session_id' label='Session ID'>
          <Input
            placeholder='Enter Session ID'
            allowClear
            type='number'
            value={formik.values.session_id}
            onChange={(e) => formik.setFieldValue('session_id', e.target.value)}
          />
        </Label>
        <Label value={formik.values.event_id} htmlFor='event_id' label='Event ID'>
          <Input
            placeholder='Enter Event ID'
            allowClear
            type='number'
            value={formik.values.event_id}
            onChange={(e) => formik.setFieldValue('event_id', e.target.value)}
          />
        </Label>
        <Label value={formik.values.event_type} htmlFor='event_type' label='Event Type'>
          <EventTypeSelector
            multiple
            value={formik.values.event_type}
            onChange={(val) => formik.setFieldValue('event_type', val)}
          />
        </Label>
        <Label value={formik.values.channels} htmlFor='channels' label='Channels'>
          <ChannelSelect
            multiple
            id='channels'
            value={formik.values.channels}
            onChange={(val) => formik.setFieldValue('channels', val)}
          />
        </Label>
        <Label value={formik.values.ad_ids?.length > 0} htmlFor='ad_ids' label='Ads'>
          <AdSelect
            multiple
            value={formik.values.ad_ids}
            onChange={(val) => formik.setFieldValue('ad_ids', val)}
          />
        </Label>
        <Label value={formik?.values?.adset_ids?.length > 0} htmlFor='adset_ids' label='Ad Sets'>
          <AdsetNameSelect
            multiple
            value={formik.values.adset_ids}
            onChange={(val) => formik.setFieldValue('adset_ids', val)}
          />
        </Label>
        <Label
          value={formik?.values?.product_ids?.length > 0}
          htmlFor='product_ids'
          label='Products'
        >
          <ProductSelector
            className='w-full'
            multiple
            value={formik.values.product_ids}
            onChange={(val) => formik.setFieldValue('product_ids', val)}
          />
        </Label>
        <Label
          value={formik?.values?.campaign_ids?.length > 0}
          htmlFor='campaign_ids'
          label='Campaigns'
        >
          <CampaignSelect
            multiple
            className='w-full'
            value={formik.values.campaign_ids}
            onChange={(val) => formik.setFieldValue('campaign_ids', val)}
          />
        </Label>
        <Label value={formik.values?.revenue?.value} htmlFor='revenue' label='Revenue'>
          <AntFilterField
            placeholder='Revenue'
            name='revenue.value'
            value={formik.values?.revenue?.value}
            type={formik.values?.revenue?.type || 'eq'}
            defaultValue={formik.values?.revenue?.type}
            onTypeChange={(val) => {
              formik.setFieldValue('revenue.type', val);
            }}
            onChange={formik.handleChange}
          />
        </Label>
        <Label value={formik.values?.value?.value} htmlFor='ltv' label='LTV Value'>
          <AntFilterField
            placeholder='LTV Value'
            name='ltv.value'
            value={formik.values?.ltv?.value}
            type={formik.values?.ltv?.type || 'eq'}
            defaultValue={formik.values?.ltv?.type}
            onTypeChange={(val) => {
              formik.setFieldValue('ltv.type', val);
            }}
            onChange={formik.handleChange}
          />
        </Label>
      </div>

      <div className='items-center justify-between gap-2 sm:flex mt-2'>
        <Button htmlType='button' onClick={handleResetForm} className='!w-fit'>
          Reset
        </Button>
        <Button type='primary' htmlType='submit' className='!w-fit ml-auto' onClick={handleFilter}>
          Filter
        </Button>
      </div>
    </form>
  );
};

export default UserJourneyFilterForm;
